// @ts-strict-ignore
import queryString from 'query-string';

import { GradFairId } from 'api/gradSchools';
import {
  Address,
  Admin,
  AdminInvite,
  AdminRequest,
  BillingAddress,
  FilestackImageData,
  ImageWithCaption,
  ReferrerCounts,
  RemoteOptionsWithFormData,
  SlideshowData,
  UserMini,
} from 'api/types/shared.types';
import { Volop } from 'api/volopsAdmin';
import { BackgroundTask } from 'utils/asyncBackendTask';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import { filterNulls } from 'utils/functional';
import { del, getJson, patchJson, postJson, putJson } from 'utils/http';
import { titleCaseToCamelCase } from 'utils/string';

export type LineItem = {
  id: string;
  created: string;
  itemTypeId?: string;
  itemType: string;
  itemId?: string;
  listingId?: string;
  listingName?: string;
  listingThirdParty?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  due: string;
  invoice: {
    id: string;
    number: string;
  };
};
export type LineItemWithPayment = LineItem & {
  paymentParts: Array<{
    id: string;
    created: string;
    amount: number;
    payment: {
      id: string;
      method: string;
      transactionId: string;
      amount: number;
    };
  }>;
};
export type Invoice = {
  id: string;
  number: number;
  created: string;
  updated: string;
  closed: string;
  billingAddress: BillingAddress;
  lineItems: Array<LineItemWithPayment>;
  creator: UserMini | null | undefined;
  paymentSettled: boolean;
  orgNote: string;
  vatNumber: string | null | undefined;
  due: string;
  totalForInvoice: string;
  acceptsPartialPayment: boolean;
  publicUrl: string;
  pendingPayment?: string;
};
type InvoiceMini = {
  id: string;
  number: number;
  created: string;
  closed: string;
  paymentSettled: boolean;
};
type ListingInvoice = InvoiceMini & {
  pendingPayment: boolean;
};
export type Org = {
  type: 'ORG';
  id: string;
  name: string;
  description: string;
  email: string;
  phone: string;
  areasOfFocus: Array<string>;
  is501c3: boolean;
  orgType: string;
  created: string;
  isPublished: boolean;
  isApproved: boolean;
  isAllowedToPost: boolean;
  isFrozen: boolean;
  paysForListings: boolean;
  canPayByCheck: boolean;
  canPostVolops: boolean;
  canPostGradProgram: boolean;
  canBuyCredits: boolean;
  canDeleteOrg: boolean;
  canAddAdmins: boolean;
  canAddOwners: boolean;
  hasInvoices: boolean;
  hasApi: boolean;
  hasAts: boolean;
  hasAtsDt: string | null | undefined;
  orgSize?: string;
  preferredListingType?: string;
  relatedJobAreas?: boolean;
  relatedJobCount?: number;
  clients?: string;
  services?: string;
  isBCorp?: boolean;
  canPostAnonymously: boolean;
  postingRequiresApproval: boolean;
  hasGradProgram: boolean;
  gradProgramLabel: ('EXPIRES_SOON' | null | undefined) | 'EXPIRED';
  locale: string;
  url: Record<UserLocale, string> | null | undefined;
  address: Address;
  governmentId: string | null | undefined;
  vatNumber: string | null | undefined;
  linkedinId: number | null | undefined;
  mustProvideVat: boolean;
  links: Array<string>;
  logo: FilestackImageData | null | undefined;
  banner: FilestackImageData | null | undefined;
  images: Array<ImageWithCaption>;
  featureFairRegistrations: boolean;
  featureFairLeads: boolean;
  fairContacts: number;
  admins: Array<Admin>;
  adminRequests: Array<AdminRequest>;
  invites: Array<AdminInvite>;
  invoices: Array<InvoiceMini>;
  frozenListings: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  itemsInCart: number;
  credits: {
    JOB: number;
    INTERNSHIP: number;
  };
  promoCredits: {
    JOB: number;
    INTERNSHIP: number;
    GRAD_PROGRAM: number;
  };
  searchApiKey: string;
  jazzhr:
    | {
        customerId: string;
        autoPublish: boolean;
        byUser:
          | {
              id: string;
              name: string;
              email: string;
            }
          | null
          | undefined;
      }
    | null
    | undefined;
  greenhouse:
    | {
        boardToken: string;
        autoPublish: boolean;
        byUser:
          | {
              id: string;
              name: string;
              email: string;
            }
          | null
          | undefined;
      }
    | null
    | undefined;
  prices: {
    listings: {
      JOB: number;
      INTERNSHIP: number;
    };
    GRAD_PROGRAM: string;
    CREDIT_PACKS: {
      type: string;
      price: number;
      quantity: number;
    };
  };
  tuitionCustomItems: Array<string>;
  admissionCustomItems: Array<string>;
  studentBodyCustomItems: Array<string>;
  recommendationsEnabled: boolean;
  orgDataHash: string;
  surveyBannerEnabled: boolean;
};
export type Job = {
  type: 'JOB';
  id: string;
  name: string;
  locale: UserLocale;
  hasOrgNameOverride: boolean;
  orgNameOverride: string | null | undefined;
  orgLogoOverride: FilestackImageData | null | undefined;
  isPostedAnonymously: boolean;
  status: Array<string>;
  created: string;
  updated: string;
  deleted: string | null | undefined;
  isEditable: boolean;
  isPublished: boolean;
  firstPublished: string | null | undefined;
  isDraft: boolean;
  isArchived: boolean;
  isFrozen: boolean;
  isHidden: boolean;
  canBeHidden: boolean;
  canBeUnhidden: boolean;
  canBeReposted: boolean;
  canBePosted: boolean;
  creator: UserMini | null | undefined;
  repostFrom:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  description: string;
  slug: string;
  expires: string;
  address: Address;
  hasAts: boolean;
  hasAtsDt: string | null | undefined;
  requestedAttachments: Array<{
    label: string;
    isRequired: boolean;
    type: 'FILE' | 'URL';
  }>;
  applyAck: string | null | undefined;
  applyEmail: string | null | undefined;
  applyUrl: string | null | undefined;
  applyText: string | null | undefined;
  benefits: string | null | undefined;
  functions: Array<string>;
  url: Record<UserLocale, string>;
  invoice: ListingInvoice | null | undefined;
  applicationDeadline: string | null | undefined;
  remoteOptions: RemoteOptionsWithFormData;
  startDate: string | null | undefined;
  salaryMinimum: number | null | undefined;
  salaryMaximum: number | null | undefined;
  salaryDetails: string | null | undefined;
  salaryCurrency: string | null | undefined;
  salaryPeriod: string | null | undefined;
  professionalLevel: string;
  languageProficiency: string | null | undefined;
  education: string;
  isFullTime: boolean;
  isTemporary: boolean;
  isContract: boolean;
  applicationCount: number;
  uniqueViews: number;
  referrerCounts?: ReferrerCounts;
  importedFrom?: string;
  hasSuggestedApplicants: boolean;
  org?: Org;
  synonymId?: string;
  synonymMatchScore?: string;
};
export type Internship = {
  type: 'INTERNSHIP';
  id: string;
  name: string;
  locale: UserLocale;
  hasOrgNameOverride: boolean;
  orgNameOverride: string | null | undefined;
  orgLogoOverride: FilestackImageData | null | undefined;
  isPostedAnonymously: boolean;
  status: Array<string>;
  created: string;
  updated: string;
  deleted: string | null | undefined;
  isEditable: boolean;
  isPublished: boolean;
  firstPublished: string | null | undefined;
  isDraft: boolean;
  isArchived: boolean;
  isFrozen: boolean;
  isHidden: boolean;
  canBeHidden: boolean;
  canBeUnhidden: boolean;
  canBeReposted: boolean;
  canBePosted: boolean;
  creator: UserMini | null | undefined;
  repostFrom:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  description: string;
  slug: string;
  expires: string;
  address: Address;
  hasAts: boolean;
  hasAtsDt: string | null | undefined;
  requestedAttachments: Array<{
    label: string;
    isRequired: boolean;
    type: 'FILE' | 'URL';
  }>;
  applyAck: string | null | undefined;
  applyEmail: string | null | undefined;
  applyUrl: string | null | undefined;
  applyText: string | null | undefined;
  benefits: string | null | undefined;
  functions: Array<string>;
  url: Record<UserLocale, string>;
  invoice: ListingInvoice | null | undefined;
  applicationDeadline: string | null | undefined;
  remoteOptions: RemoteOptionsWithFormData;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  payment: string | null | undefined;
  wage: string | null | undefined;
  academicCredits: boolean;
  paymentOk: boolean;
  hoursPerWeek: number | null | undefined;
  applicationCount: number;
  uniqueViews: number;
  referrerCounts?: ReferrerCounts;
  importedFrom?: string;
};
export type Listing = Job | Internship | Volop;
export type RepostedListing = {
  id: string;
  name: string;
  type: 'JOB' | 'INTERNSHIP' | 'VOLOP';
  firstPublished: string;
  location: string;
};
export type Payment = {
  id: string;
  created: string;
  method: string;
  transactionId: string;
  amount: string;
  last4?: string;
  parts: Array<{
    id: string;
    created: string;
    amount: string;
    lineItem: LineItem;
  }>;
};
export type CreditPack = {
  id: string;
  created: string;
  packId: string;
  invoice:
    | {
        id: string;
        number: string;
        paymentSettled: boolean;
      }
    | null
    | undefined;
  usedOn: string;
};
export type Credit = {
  id: string;
  isUsed: boolean;
  isCancelled: boolean;
  invoice:
    | {
        id: string;
        number: string;
      }
    | null
    | undefined;
  listing:
    | {
        id?: string;
        name: string;
        listingType: string;
      }
    | null
    | undefined;
  usedBy:
    | {
        id?: string;
        name?: string;
        email?: string;
        type: string;
      }
    | null
    | undefined;
  usedOn: string | null | undefined;
  usedByStaff: boolean;
};
export type GradProgramUnpaidInvoice = {
  id: string;
  closed: string;
  creator: {
    id: string;
    name: string;
  };
};
type TestimonialImage = FilestackImageData;
export type Testimonial = {
  name: string;
  text: string;
  degree: string;
  year: string | number;
  image: TestimonialImage;
  id: string | null | undefined;
};
export type GradProgram = {
  type: 'GRAD_PROGRAM';
  id: string;
  name: string;
  schoolName: string;
  description: string;
  whatsSpecial: string;
  phone: string;
  email: string;
  links: Array<string>;
  created: string;
  updated: string;
  expires: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  org: {
    id: string;
    orgType: string;
  };
  isPublished: boolean;
  firstPublished: string | null | undefined;
  isAwaitingOrgApproval: boolean;
  address: Address;
  logo: FilestackImageData | null | undefined;
  banner: FilestackImageData | null | undefined;
  slideshowItems: Array<SlideshowData>;
  applicationGRE: boolean;
  applicationGMAT: boolean;
  applicationTOEFL: boolean;
  applicationIELTS: boolean;
  applicationPTE: boolean;
  applicationLettersRecomm2: boolean;
  applicationLettersRecomm3: boolean;
  applicationAcademicTranscripts: boolean;
  applicationPersonalStatement: boolean;
  additionalRequirements: string;
  nbOfStudents: number;
  maleStudents: number;
  femaleStudents: number;
  isHistoricallyBlack: boolean;
  hasDiversityOffice: boolean;
  isMinorityServing: boolean;
  careers: string | null | undefined;
  associations: string | null | undefined;
  applicationFee: number | null | undefined;
  avgInStateTuition: number | null | undefined;
  avgOutStateTuition: number | null | undefined;
  workStudyAvailable: boolean;
  financialAidIntl: boolean;
  applicationFeeWaiver: boolean;
  avgFinancialAid: number;
  applicationDeadline: string | null | undefined;
  testimonials: Array<Testimonial>;
  isPublicSchool: boolean;
  acceptanceRate: number | null | undefined;
  url: Record<UserLocale, string> | null | undefined;
  unpaidInvoice: GradProgramUnpaidInvoice | null | undefined;
  tuitionCustomItems: Array<string>;
  studentBodyCustomItems: Array<string>;
  admissionCustomItems: Array<string>;
  canBeHidden: boolean;
  canBeUnhidden: boolean;
  isHidden: boolean;
  status: Array<string>;
  areasOfStudy: Array<string>;
  degreeProgramsOffered: Array<string>;
  fairLocations: Array<GradFairId>;
  savedCount: number;
};
export type Contact = {
  created: string;
  firstName: string;
  lastName: string;
  email: string;
  interestedInDegreeProgram: string;
  expectedEnrollmentDate: string;
  notes: string;
  location: {
    id: string;
    name: string;
  };
};
export type Integration = 'greenhouse' | 'jazzhr';
export type IntegrationJobStatus =
  | 'NOT_IMPORTED'
  | 'IMPORTED'
  | 'DONT_IMPORT'
  | 'FAILED';
export type IntegrationJob = {
  id: number;
  title: string;
  url: string;
  idealistJobId: string;
  status: IntegrationJobStatus;
};
export type EmailNotificationPreferencesInfo = {
  frequency: string;
};
export type AtsNotificationPreferencesInfo = {
  listingExpires: boolean;
  newApplication: boolean;
  newApplicationFrequency: string;
};
export const fetchOrg = (orgId: string): Promise<Org> =>
  getJson(`/data/orgdashboard/${orgId}/organization`).then(({ org }) => org);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateOrg = (orgInfo: Record<string, any>): Promise<Org> =>
  putJson(`/data/orgdashboard/${orgInfo.id}/organization`, orgInfo).then(
    ({ org }) => org
  );
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const partialUpdateOrg = (orgInfo: Record<string, any>): Promise<Org> =>
  patchJson(`/data/orgdashboard/${orgInfo.id}/organization`, orgInfo).then(
    ({ org }) => org
  );
export function deleteOrg(orgId: string) {
  return del(`/data/orgdashboard/${orgId}/organization`);
}
export function addOwner(
  orgId: string,
  email: string
): Promise<{
  user: Admin | null | undefined;
  invite: AdminInvite | null | undefined;
}> {
  return postJson(`/data/orgdashboard/${orgId}/owners`, {
    email,
  });
}
export function addAdmin(
  orgId: string,
  email: string
): Promise<{
  user: Admin | null | undefined;
  invite: AdminInvite | null | undefined;
}> {
  return postJson(`/data/orgdashboard/${orgId}/admins`, {
    email,
  });
}
export function removeAdmin(orgId: string, userId: string) {
  return del(`/data/orgdashboard/${orgId}/admins/${userId}`);
}
export function approveAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: true,
  }).then(({ admin }) => admin);
}
export function rejectAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: false,
  });
}
export const updateVatNumber = (orgId: string, vatNumber: string) =>
  putJson(`/data/orgdashboard/${orgId}/organization/vat`, {
    vatNumber,
  }).then(({ org }) => org);
export const deleteBillingAddress = (orgId: string, addressId: string) =>
  del(`/data/orgdashboard/${orgId}/billing-addresses/${addressId}`);
export function createJob(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listings: Array<Record<string, any>>;
  requiresPayment: boolean;
}> {
  return postJson(`/data/orgdashboard/${orgId}/jobs`, payload).then(
    ({ jobs: listings, requiresPayment }) => ({
      listings,
      requiresPayment,
    })
  );
}
export function validateJob(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  job: Job;
}> {
  return postJson(`/data/orgdashboard/${orgId}/jobs-validation`, payload);
}
export function updateJob(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  job: Job;
  requiresPayment: boolean;
  hasInvoice: true;
}> {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return putJson(`/data/orgdashboard/${orgId}/jobs/${payload.id}`, payload);
}
export function createInternship(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listings: Array<Record<string, any>>;
  requiresPayment: boolean;
}> {
  return postJson(`/data/orgdashboard/${orgId}/internships`, payload).then(
    ({ internships: listings, requiresPayment }) => ({
      listings,
      requiresPayment,
    })
  );
}
export function validateInternship(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  internship: Internship;
}> {
  return postJson(
    `/data/orgdashboard/${orgId}/internships-validation`,
    payload
  );
}
export function updateInternship(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  internship: Internship;
  requiresPayment: boolean;
  hasInvoice: true;
}> {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return putJson(
    `/data/orgdashboard/${orgId}/internships/${payload.id}`,
    payload
  );
}
export function createVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  return postJson(`/data/orgdashboard/${orgId}/volops`, payload);
}
export function validateVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  return postJson(`/data/orgdashboard/${orgId}/volops-validation`, payload);
}
export function updateVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return putJson(`/data/orgdashboard/${orgId}/volops/${payload.id}`, payload);
}
export function fetchListing(
  orgId: string,
  listingType: string,
  listingId: string
): Promise<Job | Internship | Volop> {
  return getJson(
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}
export function hideListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
  return postJson(
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}/hide`
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}
export function unhideListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
  return postJson(
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}/unhide`
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}
export function deleteListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  return del(
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}
export function fetchRepostedListings(
  orgId: string
): Promise<Array<RepostedListing>> {
  return getJson(`/data/orgdashboard/${orgId}/published-reposts`).then(
    ({ listings }) => listings
  );
}
export const fetchInvoices = (
  orgId: string,
  filters: {
    number: string | null | undefined;
    before: string | null | undefined;
    after: string | null | undefined;
    outstandingOnly?: boolean;
    pageIndex: number;
    perPage: number;
  }
): Promise<{
  invoices: Array<Invoice>;
  count: number;
  perPage: number;
  nbPages: number;
}> => {
  const { number, before, after, outstandingOnly, pageIndex, perPage } =
    filters;
  const params = {
    number,
    before,
    after,
    outstanding: !!outstandingOnly,
    page: pageIndex,
    perPage,
  };
  return getJson(
    `/data/orgdashboard/${orgId}/invoices?${queryString.stringify(params)}`
  );
};
export const fetchOpenInvoices = (orgId: string): Promise<Array<Invoice>> =>
  getJson(`/data/orgdashboard/${orgId}/open-invoices`).then(
    ({ invoices }) => invoices
  );
export function fetchInvoice(
  orgId: string,
  invoiceId: string
): Promise<Invoice> {
  return getJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}`).then(
    ({ invoice }) => invoice
  );
}
export function updateInvoiceOrgNote(
  orgId: string,
  invoiceId: string,
  orgNote: string
): Promise<void> {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return putJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}/org-note`, {
    orgNote,
  });
}
export function updateInvoiceBillingAddress(
  orgId: string,
  invoiceId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<void> {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  return putJson(
    `/data/orgdashboard/${orgId}/invoices/${invoiceId}/billing-address`,
    payload
  );
}
export function payInvoiceByCheck(
  orgId: string,
  invoiceId: string
): Promise<Invoice> {
  return postJson(`/data/orgdashboard/${orgId}/pay-by-check`, {
    invoiceIds: [invoiceId],
  });
}
export type PayInvoiceByCreditCardResult = {
  success: boolean;
  paymentPending?: boolean;
  invoiceIds?: Array<string>;
  errorHuman?: string;
  error?: {
    msg: string;
    stripeCode: string;
  };
};
export function payInvoicesByCreditCard(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<BackgroundTask<PayInvoiceByCreditCardResult>> {
  return postJson(
    `/data/orgdashboard/${orgId}/pay-by-credit-card`,
    payload
  ).then(({ task }) => task);
}
export function payLineItemsWithCredits(
  orgId: string,
  lineItemIds: Array<string>
): Promise<Invoice> {
  return postJson(`/data/orgdashboard/${orgId}/use-credits`, {
    lineItemIds,
  });
}
export const removeLineItem = (
  orgId: string,
  lineItemId: string
): Promise<Invoice> =>
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  del(`/data/orgdashboard/${orgId}/line-items/${lineItemId}`);
export const fetchBillingAddresses = (
  orgId: string
): Promise<Array<BillingAddress>> =>
  getJson(`/data/orgdashboard/${orgId}/billing-addresses`).then(
    ({ billingAddresses }) => billingAddresses
  );
export function updateCheckoutBillingAddress(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<void> {
  return postJson(
    `/data/orgdashboard/${orgId}/checkout/billing-address`,
    payload
  );
}
export const fetchPayments = (
  orgId: string,
  filters: {
    before: string | null | undefined;
    after: string | null | undefined;
    pageIndex: number;
    perPage: number;
  }
): Promise<{
  payments: Array<Payment>;
  count: number;
  perPage: number;
  nbPages: number;
}> => {
  const { before, after, pageIndex, perPage } = filters;
  const params = {
    before,
    after,
    page: pageIndex,
    perPage,
  };
  return getJson(
    `/data/orgdashboard/${orgId}/payments?${queryString.stringify(params)}`
  );
};
export function fetchPayment(
  orgId: string,
  paymentId: string
): Promise<Payment> {
  return getJson(`/data/orgdashboard/${orgId}/payments/${paymentId}`).then(
    ({ payment }) => payment
  );
}
export function fetchPaymentSummary(
  orgId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, any>> {
  return getJson(`/data/orgdashboard/${orgId}/payment-summary`).then(
    ({ report }) => report
  );
}
export const fetchCreditPacks = (orgId: string): Promise<Array<CreditPack>> =>
  getJson(`/data/orgdashboard/${orgId}/credits`).then(({ packs }) => packs);
export const fetchCreditPack = (
  orgId: string,
  packId: string
): Promise<{
  pack: CreditPack;
  credits: Array<Credit>;
}> => getJson(`/data/orgdashboard/${orgId}/credits/${packId}`);
export const fetchCreditsForPurchase = (
  orgId: string // TODO: Fix this the next time the file is edited.
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<Array<Record<string, any>>> =>
  getJson(`/data/orgdashboard/${orgId}/credits-for-purchase`).then(
    ({ forPurchase }) => forPurchase
  );
export const fetchGradProgram = (orgId: string): Promise<GradProgram> =>
  getJson(`/data/orgdashboard/${orgId}/grad-program`).then(
    ({ gradProgram }) => gradProgram
  );
export function createGradProgram(
  orgId: string,
  programInfo: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<GradProgram> {
  return postJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    ({ gradProgram }) => gradProgram
  );
}
export function updateGradProgram(
  orgId: string,
  programInfo: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<GradProgram> {
  return putJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    ({ gradProgram }) => gradProgram
  );
}
export function fetchGradFairContacts(
  orgId: string,
  page: number
): Promise<{
  contacts: Array<Contact>;
  lastPage: number;
  perPage: number;
}> {
  // 1-indexed pages
  const url = `/data/orgdashboard/${orgId}/gradfair/contacts?page=${page}`;
  return getJson(url);
}
export function getIntegrationJobs(
  orgId: string,
  type: Integration
): Promise<{
  jobs: Array<IntegrationJob>;
  hasError: boolean;
}> {
  const url = `/data/orgdashboard/${orgId}/${type}/jobs`;
  return getJson(url);
}
export function fetchGradProgramContacts(
  orgId: string,
  // @ts-expect-error ts-migrate(2739) FIXME: Type '{}' is missing the following properties from... Remove this comment to see the full error message
  {
    page,
    start,
    end,
  }: {
    page: number;
    start: string;
    end: string;
  } = {}
): Promise<{
  contacts: Array<Contact>;
  lastPage: number;
  pageIndex: number;
  count: number;
  perPage: number;
}> {
  const params = queryString.stringify(
    filterNulls({
      page,
      start,
      end,
    })
  );
  const url = `/data/orgdashboard/${orgId}/grad-program/contacts?${params}`;
  return getJson(url);
}
export function fetchGradProgramContactsCsvUrl(
  orgId: string,
  // @ts-expect-error ts-migrate(2739) FIXME: Type '{}' is missing the following properties from... Remove this comment to see the full error message
  {
    start,
    end,
  }: {
    start: string;
    end: string;
  } = {}
): string {
  const params = queryString.stringify(
    filterNulls({
      start,
      end,
    })
  );
  return `/data/orgdashboard/${orgId}/grad-program/contacts.csv?${params}`;
}
export function fetchLeadsNotificationPreferences(
  orgId: string
): Promise<string> {
  const url = `/data/orgdashboard/${orgId}/grad-program/notification-preferences`;
  return getJson(url).then(({ frequency }) => frequency);
}
export function updateLeadsNotificationPreferences(
  orgId: string,
  emailNotificationPreferencesInfo: EmailNotificationPreferencesInfo
): Promise<string> {
  return putJson(
    `/data/orgdashboard/${orgId}/grad-program/notification-preferences`,
    emailNotificationPreferencesInfo
  ).then(({ frequency }) => frequency);
}
export function fetchAtsNotificationPreferences(
  orgId: string
): Promise<AtsNotificationPreferencesInfo> {
  const url = `/data/orgdashboard/${orgId}/ats/notification-preferences`;
  return getJson(url).then(({ preferences }) => preferences);
}
export function updateAtsListingExpiresNotificationPreference(
  orgId: string,
  listingExpires: boolean
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/listing-expires`,
    {
      listingExpires,
    }
  ).then(({ preferences }) => preferences);
}
export function updateAtsNewApplicationNotificationPreference(
  orgId: string,
  newApplication: boolean
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/new-application`,
    {
      newApplication,
    }
  ).then(({ preferences }) => preferences);
}
export function updateAtsNewApplicationFrequencyNotificationPreference(
  orgId: string,
  newApplicationFrequency: string
): Promise<AtsNotificationPreferencesInfo> {
  return putJson(
    `/data/orgdashboard/${orgId}/ats/notification-preferences/new-application-frequency`,
    {
      newApplicationFrequency,
    }
  ).then(({ preferences }) => preferences);
}
export function hideGradProgram({ orgId }: { orgId: string }) {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
  return postJson(`/data/orgdashboard/${orgId}/grad-program/hide`).then(
    ({ gradProgram }) => gradProgram
  );
}
export function unhideGradProgram({ orgId }: { orgId: string }) {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
  return postJson(`/data/orgdashboard/${orgId}/grad-program/unhide`).then(
    ({ gradProgram }) => gradProgram
  );
}

export type Candidate = {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  updatedByUser: string;
  resumeUploaded: string;
  location: string;
  jobTitle: string;
  rating: {
    comments: string | null;
    id: string;
    isGood: boolean | null;
  } | null;
  adminNotifications: Array<{
    fromUserId: string;
    replyTo: Array<string>;
    sent: string;
  }>;
  views: Array<string>;
  userId: string;
};

export const fetchRecommendedCandidates = (
  orgId: string,
  jobId: string
): Promise<Array<Candidate>> =>
  getJson(
    `/data/orgdashboard/${orgId}/jobs/${jobId}/suggested-applicants`
  ).then(({ suggestedApplicants }) => suggestedApplicants);

export const fetchRecommendedCandidate = (
  applicantId: string
): Promise<Candidate> =>
  getJson(`/data/orgdashboard/suggested-applicants/${applicantId}`).then(
    ({ suggestedApplicant }) => suggestedApplicant
  );

export const rateRecommendedCandidate = (
  applicantId: string,
  { comments, isGood }: { comments: string; isGood: boolean }
) =>
  postJson(`/data/orgdashboard/suggested-applicants/${applicantId}/rate`, {
    applicantId,
    comments,
    isGood,
  });

export const contactRecommendedCandidate = (
  applicantId: string,
  { subject, body, replyTo, replyToOptional, attachJobListing }
) =>
  postJson(`/data/orgdashboard/suggested-applicants/${applicantId}/contact`, {
    attachJobListing,
    body,
    replyTo,
    replyToOptional,
    subject,
  });

export type LatestAtsUsage = {
  JOB: boolean;
  INTERNSHIP: boolean;
  VOLOP: boolean;
};

export function fetchLatestAtsUsage(orgId: string): Promise<LatestAtsUsage> {
  return getJson(`/data/orgdashboard/${orgId}/latest-listings-use-ats`).then(
    ({ latestAtsUsage }: { latestAtsUsage: LatestAtsUsage }) => latestAtsUsage
  );
}
